import React from 'react';
import { useTranslation } from 'react-i18next';
import { resolveCountryName, type UseCompaniesListReturn } from '../../hooks/useCompaniesList';
import { CompanyFormModal } from './CompanyFormModal';
import { CarrierImportModal } from './CarrierImportModal';
import { EQUIPMENT_TYPES } from './carrierCapabilities';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import { Search, Plus, Pencil, Eye, Phone, MapPin, Globe, Route, Upload } from 'lucide-react';

export const CompaniesList: React.FC<UseCompaniesListReturn> = ({
  loading,
  statusFilter,
  setStatusFilter,
  searchQuery,
  setSearchQuery,
  originCountryFilter,
  setOriginCountryFilter,
  destinationCountryFilter,
  setDestinationCountryFilter,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  filteredCompanies,
  paginatedItems,
  totalPages,
  countries,
  allDeliveryCountries,
  isModalOpen,
  setIsModalOpen,
  editingCompany,
  formData,
  setFormData,
  submitting,
  viewingCompany,
  isViewModalOpen,
  setIsViewModalOpen,
  handleOpenModal,
  handleViewDetails,
  handleSubmit,
  handleMultiSelectChange,
  equipmentTypes,
  carrierStatusFilterOptions,
  fieldErrors,
  formErrors,
  isImportModalOpen,
  setIsImportModalOpen,
  importFile,
  importReport,
  importError,
  importSubmitting,
  templateDownloading,
  guideDownloading,
  handleOpenImportModal,
  handleCloseImportModal,
  handleImportFileChange,
  handleDownloadTemplate,
  handleDownloadGuide,
  handleSubmitImport,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-3xl tracking-tight">{t('admin.companies.title')}</h2>
        
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button onClick={() => handleOpenModal()}>
            <Plus className="h-4 w-4 mr-2" />
            {t('admin.companies.add')}
          </Button>
          {/* Carrier Directory import (issue #178). Sits next to "Add
              Company" per the acceptance criteria. The button is a
              thin wrapper that hands control to the hook's open-modal
              handler — all the import work happens inside the modal
              and the report surfaces immediately after submit. */}
          <Button
            type="button"
            variant="outline"
            onClick={handleOpenImportModal}
          >
            <Upload className="h-4 w-4 mr-2" />
            {t('admin.companies.import')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4 bg-white p-4 rounded-lg border shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder={t('admin.companies.filterName')}
              className="pl-9 h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label={t('admin.companies.filterStatus')}
            className="h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="all">{t('admin.companies.filterStatus')}</option>
            {carrierStatusFilterOptions.map((status) => (
              <option key={status} value={status}>
                {t(`admin.companies.carrierStatusLabels.${status}`, status)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-row gap-4 items-center border-t pt-4">
          <select
            value={originCountryFilter}
            onChange={(e) => setOriginCountryFilter(e.target.value)}
            aria-label={t('admin.companies.filterOrigin')}
            className="h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 flex-1"
          >
            <option value="">{t('admin.companies.filterOrigin')}</option>
            {/* Options are keyed by the canonical ISO alpha2 code (#302)
                so the filter matches rows regardless of whether they
                store a code or a legacy name; the label stays the
                readable country name. Unresolved legacy values keep
                their raw form as both value and label. */}
            {countries.map(c => <option key={`origin-${c.value}`} value={c.value}>{c.label}</option>)}
          </select>
          
          <span className="text-muted-foreground text-sm">→</span>
          
          <select
            value={destinationCountryFilter}
            onChange={(e) => setDestinationCountryFilter(e.target.value)}
            aria-label={t('admin.companies.filterDestination')}
            className="h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 flex-1"
          >
            <option value="">{t('admin.companies.filterDestination')}</option>
            {allDeliveryCountries.map(c => <option key={`dest-${c.value}`} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.companies.name')}</TableHead>
                <TableHead>{t('admin.companies.route')}</TableHead>
                {/* Carrier Directory profile fields (#174). The
                    Location column surfaces postal_code + city so admins
                    can scan the directory at a glance; website and the
                    remaining profile detail are visible in the details
                    modal. */}
                <TableHead>{t('admin.companies.location')}</TableHead>
                <TableHead>{t('admin.companies.equipmentTypes')}</TableHead>
                <TableHead>{t('admin.companies.carrierStatus')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    {t('admin.companies.loading')}
                  </TableCell>
                </TableRow>
              ) : filteredCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    {t('admin.companies.noCompanies')}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedItems.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium">{company.name}</span>
                        <span className="text-xs text-muted-foreground">{company.email}</span>
                        {company.phone_number && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {company.phone_number}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5 text-sm">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          <span className="font-medium">{resolveCountryName(company.country, language)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">→</span>
                          <Globe className="h-3 w-3 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            {company.delivery_countries.length > 2 
                              ? `${company.delivery_countries.slice(0, 2).map((value) => resolveCountryName(value, language)).join(', ')} +${company.delivery_countries.length - 2}`
                              : company.delivery_countries.map((value) => resolveCountryName(value, language)).join(', ')
                            }
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {/* Location cell (#174). Surfaces the Carrier
                          Directory profile fields that admins most often
                          use to identify a carrier in the directory:
                          city and postal code. The website is shown
                          only when present so the cell stays compact
                          for carriers that haven't filled it in. */}
                      <div className="flex flex-col gap-0.5 text-sm">
                        {company.city && (
                          <span className="font-medium">{company.city}</span>
                        )}
                        {company.postal_code && (
                          <span className="text-xs text-muted-foreground">
                            {company.postal_code}
                          </span>
                        )}
                        {!company.city && !company.postal_code && (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {company.equipment_types
                          .filter((type) => EQUIPMENT_TYPES.includes(type))
                          .slice(0, 2)
                          .map(type => (
                          <Badge key={type} variant="outline" className="text-[10px]">
                            {t(`quote.methods.${type}`, type)}
                          </Badge>
                        ))}
                        {company.equipment_types.filter((type) => EQUIPMENT_TYPES.includes(type)).length > 2 && (
                          <Badge variant="outline" className="text-[10px]">
                            +{company.equipment_types.filter((type) => EQUIPMENT_TYPES.includes(type)).length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {/* Carrier Status (#173, #179) is the visible badge
                          in the directory. The row is read-only here;
                          status is edited through the existing
                          CompanyFormModal field (`carrier-status-select`).
                          The stable `carrier_status` key drives the
                          variant, the localized `carrier_status_label` is
                          the visible text. */}
                      <Badge variant={company.carrier_status === 'active' ? 'default' : 'secondary'}>
                        {company.carrier_status_label ?? company.carrier_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleViewDetails(company)}
                          title={t('admin.companies.viewDetails')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleOpenModal(company)}
                          title={t('admin.companies.edit')}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('common.items_per_page')}:
              </span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('common.page_info', { current: currentPage, total: totalPages || 1 })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Details Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('admin.companies.contactDetails')}</DialogTitle>
          </DialogHeader>
          {viewingCompany && (
            <div className="space-y-4 py-4">
              <div className="space-y-1">
                <h3 className="text-lg">{viewingCompany.name}</h3>
                <Badge variant={viewingCompany.carrier_status === 'active' ? 'default' : 'secondary'}>
                  {viewingCompany.carrier_status_label ?? viewingCompany.carrier_status}
                </Badge>
              </div>

              <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
                {viewingCompany.email && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium w-24">{t('admin.companies.email')}:</span>
                    <a href={`mailto:${viewingCompany.email}`} className="text-sm text-blue-600 hover:underline">
                      {viewingCompany.email}
                    </a>
                  </div>
                )}
                {viewingCompany.phone_number && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium w-24">{t('admin.companies.phone')}:</span>
                    <a href={`tel:${viewingCompany.phone_number}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {viewingCompany.phone_number}
                    </a>
                  </div>
                )}
                {/* Carrier Directory profile fields (#174). Each field
                    is shown only when populated so the modal stays
                    quiet for carriers that haven't filled the detail
                    in. Email is omitted entirely when blank (the new
                    directory allows email-less entries). */}
                {viewingCompany.website && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium w-24">{t('admin.companies.website')}:</span>
                    <a href={viewingCompany.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                      {viewingCompany.website}
                    </a>
                  </div>
                )}
                {viewingCompany.direct_contact && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium w-24">{t('admin.companies.directContact')}:</span>
                    <span className="text-sm">{viewingCompany.direct_contact}</span>
                  </div>
                )}
                {(viewingCompany.city || viewingCompany.postal_code) && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium w-24">{t('admin.companies.location')}:</span>
                    <span className="text-sm">
                      {[viewingCompany.city, viewingCompany.postal_code].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
                {viewingCompany.address && (
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-medium w-24">{t('admin.companies.address')}:</span>
                    <span className="text-sm">{viewingCompany.address}</span>
                  </div>
                )}
                {/* Carrier Validation (#174). Validated-by display name
                    and the validation datetime are surfaced together so
                    the operator can tell at a glance which admin last
                    confirmed the carrier's directory detail. */}
                {(viewingCompany.validated_at || viewingCompany.validated_by) && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium w-24">{t('admin.companies.validatedAt')}:</span>
                    <span className="text-sm">
                      {viewingCompany.validated_at
                        ? new Date(viewingCompany.validated_at).toLocaleString()
                        : ''}
                      {viewingCompany.validated_by ? ` — ${viewingCompany.validated_by}` : ''}
                    </span>
                  </div>
                )}
              </div>

              {(viewingCompany.notes || viewingCompany.other_equipment) && (
                <div className="space-y-3 border rounded-lg p-4 bg-white">
                  {viewingCompany.notes && (
                    <div className="space-y-1">
                      <h4 className="text-sm font-medium">{t('admin.companies.carrierNotes')}</h4>
                      <p className="text-sm whitespace-pre-wrap">{viewingCompany.notes}</p>
                    </div>
                  )}
                  {viewingCompany.other_equipment && (
                    <div className="space-y-1">
                      <h4 className="text-sm font-medium">{t('admin.companies.otherEquipment')}</h4>
                      <p className="text-sm whitespace-pre-wrap">{viewingCompany.other_equipment}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-1">
                  <Route className="h-4 w-4" />
                  {t('admin.companies.route')}
                </h4>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{resolveCountryName(viewingCompany.country, language)}</span>
                  <span className="text-muted-foreground">→</span>
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span>{viewingCompany.delivery_countries.map((value) => resolveCountryName(value, language)).join(', ')}</span>
                </div>
              </div>

              <div className="space-y-2">
                  <h4 className="text-sm font-medium">{t('admin.companies.equipmentTypes')}</h4>
                <div className="flex flex-wrap gap-1">
                  {viewingCompany.equipment_types
                    .filter((type) => EQUIPMENT_TYPES.includes(type))
                    .map(type => (
                    <Badge key={type} variant="outline">
                      {t(`quote.methods.${type}`, type)}
                    </Badge>
                  ))}
                </div>
              </div>

              {(viewingCompany.max_weight || viewingCompany.max_length || viewingCompany.max_width || viewingCompany.max_height) && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">{t('admin.companies.capacity')}</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {viewingCompany.max_weight && (
                      <div><span className="text-muted-foreground">{t('admin.companies.maxWeight')}:</span> {viewingCompany.max_weight} kg</div>
                    )}
                    {viewingCompany.max_length && (
                      <div><span className="text-muted-foreground">{t('admin.companies.maxLength')}:</span> {viewingCompany.max_length} cm</div>
                    )}
                    {viewingCompany.max_width && (
                      <div><span className="text-muted-foreground">{t('admin.companies.maxWidth')}:</span> {viewingCompany.max_width} cm</div>
                    )}
                    {viewingCompany.max_height && (
                      <div><span className="text-muted-foreground">{t('admin.companies.maxHeight')}:</span> {viewingCompany.max_height} cm</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit/Create Modal via CompanyFormModal */}
      <CompanyFormModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        editingCompany={editingCompany}
        formData={formData}
        onFormDataChange={setFormData}
        submitting={submitting}
        equipmentTypes={equipmentTypes}
        onSubmit={handleSubmit}
        onMultiSelectChange={handleMultiSelectChange}
        fieldErrors={fieldErrors}
        formErrors={formErrors}
      />

      {/* Carrier Directory import modal (issue #178). Wired through
          the hook so the row-level report and the list-cache
          invalidation both happen in the same TanStack Query flow
          that powers the rest of the page. The component only
          receives data and dispatches events. */}
      <CarrierImportModal
        isOpen={isImportModalOpen}
        onOpenChange={setIsImportModalOpen}
        file={importFile}
        onFileChange={handleImportFileChange}
        report={importReport}
        errorMessage={importError}
        submitting={importSubmitting}
        templateDownloading={templateDownloading}
        onDownloadTemplate={() => {
          void handleDownloadTemplate();
        }}
        guideDownloading={guideDownloading}
        onDownloadGuide={() => {
          void handleDownloadGuide();
        }}
        // The mutation can reject (e.g. non-CSV upload). The hook's
        // onError callback already stashes the message in
        // `importError`, so we swallow the rejection here to avoid
        // an unhandled promise — the admin sees the error through
        // the FormErrorBanner, not a thrown exception.
        onSubmit={() => {
          handleSubmitImport().catch(() => {
            /* surfaced via the hook's onError → importError */
          });
        }}
        onClose={handleCloseImportModal}
      />
    </div>
  );
};
